import { UseQueryResult } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef } from "react";
import type { ThemaCode, ThemaSearchResponse } from "reedy-data/api";

interface UseInfiniteScrollProps {
  searchQuery: UseQueryResult<ThemaSearchResponse | undefined>;
  query: string;
  activeTab: string;
  pageSize: number;
  onPageChange?: (page: number) => void;
}

export const useInfiniteScroll = ({
  searchQuery,
  query,
  activeTab,
  pageSize,
  onPageChange,
}: UseInfiniteScrollProps) => {
  const [allSearchResults, setAllSearchResults] = useState<ThemaCode[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Reset states when search query or tab changes
  useEffect(() => {
    // resets the accumulated infinite-scroll results and paging when the search query or tab changes; these drive paginated fetches so they must reset in sync with those inputs
    setAllSearchResults([]);
    setCurrentPage(1);
    setHasMorePages(true);
    setIsLoadingMore(false);
    onPageChange?.(1);
  }, [query, activeTab, onPageChange]);

  // Handle search result accumulation for infinite scrolling
  useEffect(() => {
    if (searchQuery.data?.codes && query.trim() && !searchQuery.isLoading) {
      if (currentPage === 1) {
        // First page - replace results
        // accumulates results as paginated search responses resolve from the async query; reacts to fetched data, not a synchronous mount value
        setAllSearchResults(searchQuery.data.codes);
      } else {
        // Subsequent pages - append results, but only if we have new data
        const newCodes = searchQuery.data.codes || [];
        if (newCodes.length > 0) {
          setAllSearchResults((prev) => {
            // Create a Set of existing IDs to avoid duplicates
            const existingIds = new Set(prev.map((code) => code.id));
            const uniqueNewCodes = newCodes.filter(
              (code: ThemaCode) => !existingIds.has(code.id),
            );
            return [...prev, ...uniqueNewCodes];
          });
        }
        setIsLoadingMore(false);
      }

      // Check if more pages are available
      const totalResults = searchQuery.data.total || 0;
      const loadedResults = currentPage * pageSize;
      setHasMorePages(loadedResults < totalResults);
    }
  }, [searchQuery.data, currentPage, query, searchQuery.isLoading, pageSize]);

  // Load more function for infinite scrolling
  const loadMore = useCallback(() => {
    if (
      !isLoadingMore &&
      hasMorePages &&
      !searchQuery.isLoading &&
      query.trim()
    ) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      onPageChange?.(nextPage);
    }
  }, [
    isLoadingMore,
    hasMorePages,
    searchQuery.isLoading,
    query,
    currentPage,
    onPageChange,
  ]);

  // Scroll detection for infinite scrolling
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const threshold = 100; // Load more when 100px from bottom

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMore();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  const reset = () => {
    setAllSearchResults([]);
    setCurrentPage(1);
    setHasMorePages(true);
    setIsLoadingMore(false);
    onPageChange?.(1);
  };

  return {
    allSearchResults,
    currentPage,
    hasMorePages,
    isLoadingMore,
    scrollContainerRef,
    reset,
  };
};
